// Pure, in-browser evidence preparation. No storage, provider, or network dependency.
export function parseReviewCsv(text: string): string[][] {
  if (text.length > 2_000_000) throw new Error('Choose a CSV smaller than 2 MB.');
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let quoted = false;
  const push = () => { row.push(cell); cell = ''; };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i++; }
      else quoted = !quoted;
    } else if (c === ',' && !quoted) push();
    else if ((c === '\n' || c === '\r') && !quoted) {
      if (c === '\r' && text[i + 1] === '\n') i++;
      push(); if (row.some(v => v.trim())) rows.push(row); row = [];
    } else cell += c;
  }
  if (quoted) throw new Error('The CSV contains an unfinished quoted field.');
  push(); if (row.some(v => v.trim())) rows.push(row);
  if (rows.length < 2 || rows[0].length < 2) throw new Error('Expected a header and student rows.');
  if (rows.length > 2000 || rows[0].length > 2000) throw new Error('This CSV is too large for review preparation.');
  return rows;
}

export function selectedCsvEvidence(rows: string[][], index: number) {
  if (!Number.isInteger(index) || index < 1 || index >= rows.length) throw new Error('Select a student row.');
  return rows[0].slice(1).flatMap((title, column) => {
    const value = rows[index][column + 1]?.trim();
    return value ? [{ title: title.trim(), value }] : [];
  });
}

export type CsvCourseProgress = {
  kind: 'completed' | 'reported';
  code: string;
  title: string;
  status: string;
  sourceHeader: string;
  rangeStart: string;
  rangeEnd: string;
  gaps: Array<{ code: string; title: string; status: string }>;
  lowScores: Array<{ code: string; title: string; score: number }>;
  missingScores: Array<{ code: string; title: string }>;
};

const sectionHeader = /(?:^|\s)(\d+(?:\.\d+){1,3})(?=\s|$|[-–—:])/;

function completionStatus(value: string) {
  const normalized = value.trim().toLowerCase();
  if (['complete', 'completed', 'passed'].includes(normalized)) return true;
  const percent = normalized.match(/^100(?:\.0+)?\s*%$/);
  return Boolean(percent);
}

function percentScore(value: string) {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*%$/);
  return match ? Number(match[1]) : null;
}

function compactIssues(items: Array<{ code: string; title: string; status?: string; score?: number }>) {
  const visible = items.slice(0, 12).map((item) => `${item.code}${item.title ? ` ${item.title}` : ''}${item.score !== undefined ? ` (${item.score}%)` : item.status ? ` (${item.status})` : ''}`);
  return `${visible.join('; ')}${items.length > visible.length ? `; and ${items.length - visible.length} more` : ''}`;
}

// CompTIA exports are wide rows whose course items appear in course order. We only
// call an item completed when the CSV says so explicitly; other nonblank results
// remain useful as the furthest reported section without overstating completion.
export function summarizeCsvCourseProgress(rows: string[][], index: number, expectedRangeText = ''): CsvCourseProgress | null {
  if (!Number.isInteger(index) || index < 1 || index >= rows.length) throw new Error('Select a student row.');
  const candidates = rows[0].slice(1).flatMap((header, column) => {
    const value = rows[index][column + 1]?.trim();
    const match = header.trim().match(sectionHeader);
    if (!match) return [];
    const code = match[1];
    const codeAt = header.indexOf(code);
    const title = header.slice(codeAt + code.length).replace(/^\s*[-–—:]?\s*/, '').trim();
    const explicitType = header.trim().match(/^(Fact Sheet|Video Lesson|Assessment|Lab|Activity)\s*-/i);
    const assessment = explicitType ? explicitType[1].toLowerCase() === 'assessment'
      : /\b(?:quiz|exam|assessment|test|review)\b/i.test(title);
    const score = percentScore(value || '');
    return [{ code, title, status: value || '', sourceHeader: header.trim(), assessment, score,
      completed: completionStatus(value || '') && !(assessment && score !== null) }];
  });
  const reportedCandidates = candidates.filter((candidate) => candidate.status);
  if (!reportedCandidates.length) return null;
  const completedCandidates = candidates.filter((candidate) => candidate.completed);
  const completed = completedCandidates[completedCandidates.length - 1];
  const selected = completed || reportedCandidates[reportedCandidates.length - 1];
  const expectedCodes = Array.from(expectedRangeText.matchAll(/(?:^|\s)(\d+(?:\.\d+){1,3})(?=\s|$|[-–—:,])/g), (match) => match[1]);
  const firstReportedIndex = candidates.findIndex((candidate) => candidate.status);
  const codeIndex = (code: string) => candidates.findIndex((candidate) => candidate.code === code || candidate.code.startsWith(`${code}.`));
  const expectedStartIndex = expectedCodes.length > 1 ? codeIndex(expectedCodes[0]) : -1;
  const expectedEndIndex = expectedCodes.length ? codeIndex(expectedCodes[expectedCodes.length - 1]) : -1;
  const selectedIndex = candidates.indexOf(selected);
  const startIndex = expectedStartIndex >= 0 ? expectedStartIndex : firstReportedIndex;
  const lastReportedIndex = candidates.indexOf(reportedCandidates[reportedCandidates.length - 1]);
  const endIndex = expectedEndIndex >= startIndex ? expectedEndIndex : Math.max(selectedIndex, lastReportedIndex);
  const range = candidates.slice(startIndex, Math.max(startIndex, endIndex) + 1);
  const gaps = range.filter((candidate) => !candidate.completed && !(candidate.assessment && candidate.score !== null)).map((candidate) => ({
    code: candidate.code, title: candidate.title, status: candidate.status || 'missing',
  }));
  const lowScores = range.filter((candidate) => candidate.assessment && candidate.score !== null && candidate.score < 80).map((candidate) => ({
    code: candidate.code, title: candidate.title, score: candidate.score as number,
  }));
  const missingScores = range.filter((candidate) => candidate.assessment && candidate.score === null).map((candidate) => ({
    code: candidate.code, title: candidate.title,
  }));
  return {
    kind: completed ? 'completed' : 'reported',
    code: selected.code,
    title: selected.title,
    status: selected.status,
    sourceHeader: selected.sourceHeader,
    rangeStart: range[0]?.code || selected.code,
    rangeEnd: range[range.length - 1]?.code || selected.code,
    gaps,
    lowScores,
    missingScores,
  };
}

export function csvProgressEvidence(rows: string[][], index: number, expectedRangeText = '') {
  const progress = summarizeCsvCourseProgress(rows, index, expectedRangeText);
  if (!progress) return selectedCsvEvidence(rows, index);
  return [
    {
      title: progress.kind === 'completed' ? 'Furthest explicitly completed course section' : 'Furthest course section with a reported result',
      value: `${progress.code}${progress.title ? ` — ${progress.title}` : ''}`,
    },
    { title: 'What this section covered', value: progress.title || 'The CSV did not include a section title.' },
    { title: 'CSV-reported section status', value: progress.status },
    {
      title: `Sequence check · ${progress.rangeStart} through ${progress.rangeEnd}`,
      value: progress.gaps.length ? `${progress.gaps.length} incomplete or missing course items: ${compactIssues(progress.gaps)}` : 'No incomplete course items were found in this range.',
    },
    {
      title: 'Assessment score check',
      value: [
        progress.lowScores.length ? `Below 80%: ${compactIssues(progress.lowScores)}` : 'No assessment scores below 80% were found.',
        progress.missingScores.length ? `Missing scores: ${compactIssues(progress.missingScores)}` : 'No missing assessment scores were found.',
      ].join(' '),
    },
    ...selectedCsvEvidence(rows, index),
  ];
}

const csvSummaryTitles = [
  'Furthest explicitly completed course section',
  'Furthest course section with a reported result',
  'What this section covered',
  'CSV-reported section status',
  'Assessment score check',
];

// CSV exports can contain thousands of columns. The five calculated records above
// already scan the complete row and preserve the exact endpoint, gaps, and score
// checks. Keep those plus a bounded set of recent raw results for the local model.
export function compactExternalEvidence<T extends { records?: Array<{ title: string; value: string }> }>(sources: T[]): T[] {
  return sources.map((source) => {
    const records = source.records || [];
    if (records.length <= 160) return source;
    const summaries = records.filter((record) => csvSummaryTitles.includes(record.title) || record.title.startsWith('Sequence check ·') || record.title.startsWith('CompTIA paired report · '));
    const summarySet = new Set(summaries);
    const recent = records.filter((record) => !summarySet.has(record)).slice(-120);
    return { ...source, records: [...summaries, ...recent] };
  });
}

// Only educational source groups are included, never teacher_private_note or review snapshots.
export function reportGroups(context: Record<string, any>, selfReview: Record<string, any>) {
  return [
    { title: 'Goals and milestones', source: 'Goal records · completion is student-reported', records: context.cycle_goals?.length ? context.cycle_goals.map((g: any) => ({ id: g.id, title: g.title, target: g.target, milestones: g.milestones })) : [context.technical_goal, ...(context.milestones || [])].filter(Boolean) },
    { title: 'Daily plans and wrap-ups', source: 'Student-reported daily work', records: context.progress_summary?.daily_work || context.daily_priorities || [] },
    { title: 'Assignments and assessment records', source: 'Saved classroom records · not an official grade', records: context.progress_summary?.work || [] },
    { title: 'Submitted evidence and teacher feedback', source: 'Work evidence · verification shown per record', records: context.cycle_goals?.length ? context.cycle_goals.flatMap((g: any) => g.work_evidence || []) : context.work_evidence || [] },
    { title: 'Student reflection', source: 'Student perspective · submission date may be after the period', records: selfReview.submitted ? [selfReview] : [] },
    { title: 'Teacher observations and coaching', source: 'Recorded observations and shared coaching outcomes', records: [...(context.student_visible_nsr_observations || []), ...(context.check_ins || []), ...(context.coaching_check_ins || [])] },
  ];
}
